import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Campaign, PCAMapReportResponseMinimal } from 'c-sdk';
import type { HeatmapLayerProps } from '@react-google-maps/api';
import { GoogleMap, Marker, MarkerClusterer } from '@react-google-maps/api';
import { DeckHeatmapLayer } from 'c-components/DeckHeatmapLayer';
import type { MarkerLocation } from 'c-main/Lib';
import {
    generateHeatMapData,
    generateMarkerLocations,
    getCampaignReportingMapCenter,
    getCampaignReportingMapZoom,
    ReportingMapOptions,
} from 'c-main/Lib';
import type { MapDataType } from 'c-main/Components/Campaign/CampaignReports/types';
import { useEntityData } from 'c-data';
import { useTheme } from '@mui/styles';
import type { SummaryMapDisplayMode } from './summaryCustomizationConfig.main';

type Props = {
    id: number;
    data: PCAMapReportResponseMinimal['reportData'];
    mapDataType: MapDataType;
    mapDisplayMode: SummaryMapDisplayMode;
};

const containerStyle = {
    width: '100%',
    height: '80vh',
    borderRadius: '4px',
};

const markerIconPath =
    'M3 5h18v12H3z, ' +
    'M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 ' +
    '1.99-2L23 5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z';

const ImpactsMapView: React.FC<Props> = ({ id, data, mapDataType, mapDisplayMode }) => {
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });
    const theme = useTheme();
    const mapRef = useRef<google.maps.Map | null>();

    const boundingBoxData = useMemo<google.maps.LatLngBounds>(() => {
        const bounds = new google.maps.LatLngBounds();
        Object.keys(data).forEach(key => {
            const [actual, expected, performance, inSchedule, outSchedule, unbooked, lat, lng] =
                data[key];
            bounds.extend(new google.maps.LatLng(Number(lat), Number(lng)));
        });

        // If bounds are too small (i.e., single point or very close points)
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        // Calculate the current size
        const latDiff = Math.abs(ne.lat() - sw.lat());
        const lngDiff = Math.abs(ne.lng() - sw.lng());

        // If the area is too small, extend it
        const minSize = 1; // Adjust this value to control minimum map view size
        if (latDiff < minSize || lngDiff < minSize) {
            const center = bounds.getCenter();
            bounds.extend(
                new google.maps.LatLng(center.lat() + minSize / 2, center.lng() + minSize / 2),
            );
            bounds.extend(
                new google.maps.LatLng(center.lat() - minSize / 2, center.lng() - minSize / 2),
            );
        }

        return bounds;
    }, [data]);

    const heatMapData = useMemo<HeatmapLayerProps['data']>(
        () => generateHeatMapData(data, mapDataType),
        [data, mapDataType],
    );

    const onMapLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map;
            map.fitBounds(boundingBoxData);
        },
        [boundingBoxData],
    );

    useEffect(() => {
        const map = mapRef.current;
        if (map && boundingBoxData) {
            // Add a small delay to ensure map is ready
            setTimeout(() => {
                if (mapRef.current) {
                    // Check again in case map was unmounted
                    mapRef.current.fitBounds(boundingBoxData);
                }
            }, 100);
        }
    }, [boundingBoxData]);

    const onMapUnmount = useCallback(() => {
        mapRef.current = null;
    }, []);

    const markerLocations = useMemo<MarkerLocation[]>(
        () => generateMarkerLocations(data, mapDataType),
        [data, mapDataType],
    );
    const center = useMemo(() => getCampaignReportingMapCenter(campaign), [campaign]);
    const zoom = useMemo(() => getCampaignReportingMapZoom(campaign), [campaign]);
    const repositionedOnce = useRef(false);

    useEffect(() => {
        if (markerLocations.length > 0 && !repositionedOnce.current && mapRef?.current) {
            // basically repositioning the map on first load when the first set of markers (screens) are loaded
            const bounds = new google.maps.LatLngBounds();
            markerLocations.forEach(loc => {
                if (loc.location != null) bounds.extend(loc.location);
            });

            // setTimeout(() => mapRef?.current?.fitBounds?.(bounds), 1000);
            repositionedOnce.current = true;
        }
    }, [markerLocations]);

    const heatmapLayer = useMemo(() => <DeckHeatmapLayer data={heatMapData} />, [heatMapData]);

    const showHeatmap = mapDisplayMode === 'heatmap' || mapDisplayMode === 'both';
    const showMarkers = mapDisplayMode === 'screens' || mapDisplayMode === 'both';
    const showAllScreenMarkers = mapDisplayMode === 'screens';
    const markers = useMemo(
        () => (
            <MarkerClusterer
                maxZoom={14}
                options={{ imagePath: '/mapclustericons/m' }}
                ignoreHidden
            >
                {markerClusterer => (
                    <>
                        {markerLocations.map(({ frameId, location, hidden }) => (
                            <Marker
                                key={`${frameId}-${location?.lat?.()}-${location?.lng?.()}`}
                                position={location}
                                visible={showAllScreenMarkers || !hidden}
                                clusterer={markerClusterer}
                                icon={{
                                    path: markerIconPath,
                                    fillColor: theme.palette.primary.main,
                                    strokeColor: theme.palette.primary.contrastText,
                                    fillOpacity: 1,
                                    scale: 1,
                                    anchor: new google.maps.Point(12.5, 25),
                                }}
                            />
                        ))}
                    </>
                )}
            </MarkerClusterer>
        ),
        [markerLocations, showAllScreenMarkers, theme],
    );

    return (
        <>
            <GoogleMap
                ref={mapRef as any}
                onLoad={onMapLoad}
                onUnmount={onMapUnmount}
                // zoom={zoom}
                // center={center}
                mapContainerStyle={containerStyle}
                options={ReportingMapOptions}
            >
                {showHeatmap && heatmapLayer}
                {showMarkers && markers}
            </GoogleMap>
        </>
    );
};

export default ImpactsMapView;
